import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { Api } from '@api/api';
import { getBanner } from '@api/functions';
import type { BannerItemDto } from '@api/models';
import { ProductLabelTypeEnum } from '@api/models';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import { CarouselComponent } from '../carousel/carousel.component';

type BannerItemWithDiscount = BannerItemDto & { discountValue?: number };

@Component({
  selector: 'banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, CarouselComponent, RouterLink, RouterLinkWithLang],
})
export class BannerComponent {
  private readonly api = inject(Api);
  private readonly destroyRef = inject(DestroyRef);

  readonly banner = signal<BannerItemWithDiscount[]>([]);
  readonly itemsPerSlide = 2;

  constructor() {
    this.api.invoke(getBanner)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const data = response.data ?? [];
          data.forEach(bannerItem => this.setDiscountValue(bannerItem));
          this.banner.set(data);
        },
      });
  }

  setBannerItemSrc(bannerItem: BannerItemDto): string {
    const bannerItemUrl = bannerItem?.media?.variantsUrls?.medium;
    return bannerItemUrl;
  }

  getLabelClass(bannerItem: BannerItemDto): string | undefined {
    switch (bannerItem?.label?.type) {
      case ProductLabelTypeEnum.New:
        return 'banner-label-new';
      case ProductLabelTypeEnum.Top:
        return 'banner-label-top';
      case ProductLabelTypeEnum.Sale:
        return 'banner-label-sale';
      default:
        return undefined;
    }
  }

  private setDiscountValue(bannerItem: BannerItemWithDiscount): void {
    if (!bannerItem.oldPrice) {
      return;
    }
    bannerItem.discountValue = Math.ceil(
      ((bannerItem.oldPrice - bannerItem.price) / bannerItem.oldPrice) * 100
    );
  }
}
